import { Decimal, DECIMALES } from './decimal';

/**
 * Formatage français, place marocaine (charte d'interface, doc 06, § 5).
 *
 * Module UNIQUE : aucun composant ne reformate un nombre pour son compte.
 * Les montants transitent en chaîne de caractères entre l'API et l'interface
 * (un `number` JSON perdrait la précision) — toutes les fonctions acceptent
 * donc `string | Decimal`.
 */

/** Espace fine insécable — séparateur de milliers. */
export const ESPACE_FINE = ' ';
/** Espace insécable — devant %, MAD, pts. */
export const ESPACE_INSECABLE = ' ';
/** Signe moins typographique (U+2212), jamais le trait d'union. */
export const MOINS = '−';
/** Cadratin : valeur absente. Jamais une chaîne vide, jamais zéro. */
export const VALEUR_ABSENTE = '—';

export const GLYPHE = {
  hausse: '▲',
  baisse: '▼',
  nul: '—',
} as const;

export type Numerique = string | Decimal | null | undefined;

function versDecimal(valeur: Numerique): Decimal | null {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const d = valeur instanceof Decimal ? valeur : new Decimal(valeur);
  return d.isFinite() ? d : null;
}

/**
 * Cœur du formatage : groupes de milliers séparés par une espace fine,
 * virgule décimale, signe moins typographique.
 */
function formaterNombre(d: Decimal, decimales: number, signeExplicite = false): string {
  const negatif = d.isNegative() && !d.isZero();
  const fixe = d.abs().toFixed(decimales);
  const [entiere = '0', fraction] = fixe.split('.');

  let groupee = '';
  for (let i = 0; i < entiere.length; i++) {
    if (i > 0 && (entiere.length - i) % 3 === 0) groupee += ESPACE_FINE;
    groupee += entiere[i];
  }

  const corps = fraction ? `${groupee},${fraction}` : groupee;
  if (negatif) return `${MOINS}${corps}`;
  if (signeExplicite && !d.isZero()) return `+${corps}`;
  return corps;
}

/** Montant à 2 décimales, sans devise : `1 284 500,00` */
export function formaterMontant(valeur: Numerique): string {
  const d = versDecimal(valeur);
  return d ? formaterNombre(d, DECIMALES.montant) : VALEUR_ABSENTE;
}

/** Montant suffixé de la devise : `1 284 500,00 MAD` */
export function formaterMontantDevise(valeur: Numerique, devise = 'MAD'): string {
  const d = versDecimal(valeur);
  return d ? `${formaterNombre(d, DECIMALES.montant)}${ESPACE_INSECABLE}${devise}` : VALEUR_ABSENTE;
}

/** Pourcentage à 2 décimales : `12,50 %` */
export function formaterPourcentage(valeur: Numerique, decimales = 2): string {
  const d = versDecimal(valeur);
  return d ? `${formaterNombre(d, decimales)}${ESPACE_INSECABLE}%` : VALEUR_ABSENTE;
}

/**
 * Cumul d'écarts en points, sans signe : `16,00 pts`. Zéro s'affiche — un
 * mandat parfaitement aligné n'est pas une valeur absente.
 */
export function formaterPointsCumules(valeur: Numerique, decimales = 2): string {
  const d = versDecimal(valeur);
  if (!d) return VALEUR_ABSENTE;
  return `${formaterNombre(d, decimales)}${ESPACE_INSECABLE}pts`;
}

/** Écart en points, signe toujours explicite : `+1,25 pts` / `−0,88 pts` */
export function formaterPoints(valeur: Numerique, decimales = 2): string {
  const d = versDecimal(valeur);
  if (!d) return VALEUR_ABSENTE;
  if (d.isZero()) return VALEUR_ABSENTE;
  return `${formaterNombre(d, decimales, true)}${ESPACE_INSECABLE}pts`;
}

/** Performance signée : `+7,42 %` */
export function formaterPerformance(valeur: Numerique, decimales = 2): string {
  const d = versDecimal(valeur);
  if (!d) return VALEUR_ABSENTE;
  return `${formaterNombre(d, decimales, true)}${ESPACE_INSECABLE}%`;
}

/** Quantité de titres : entier, sans décimale. */
export function formaterQuantite(valeur: Numerique): string {
  const d = versDecimal(valeur);
  return d ? formaterNombre(d, 0) : VALEUR_ABSENTE;
}

/** Parts d'OPCVM : 4 décimales. */
export function formaterParts(valeur: Numerique, decimales = DECIMALES.parts): string {
  const d = versDecimal(valeur);
  return d ? formaterNombre(d, decimales) : VALEUR_ABSENTE;
}

/** Prix ou VL : 2 décimales par défaut, 6 possibles. */
export function formaterPrix(valeur: Numerique, decimales = 2): string {
  const d = versDecimal(valeur);
  return d ? formaterNombre(d, decimales) : VALEUR_ABSENTE;
}

/* ── Dates ──────────────────────────────────────────────────────────── */

function versDate(valeur: Date | string | null | undefined): Date | null {
  if (!valeur) return null;
  const d = valeur instanceof Date ? valeur : new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}

function deuxChiffres(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `JJ/MM/AAAA` */
export function formaterDate(valeur: Date | string | null | undefined): string {
  const d = versDate(valeur);
  if (!d) return VALEUR_ABSENTE;
  return `${deuxChiffres(d.getDate())}/${deuxChiffres(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** `JJ/MM/AAAA à HH:MM` */
export function formaterDateHeure(valeur: Date | string | null | undefined): string {
  const d = versDate(valeur);
  if (!d) return VALEUR_ABSENTE;
  return `${formaterDate(d)} à ${deuxChiffres(d.getHours())}:${deuxChiffres(d.getMinutes())}`;
}

/**
 * Date du jour au format attendu par un `<input type="date">` : `AAAA-MM-JJ`.
 *
 * Construite sur l'heure locale, jamais sur `toISOString()`, qui bascule en
 * UTC : le Maroc étant à UTC+1, une saisie faite dans la première heure de
 * la journée serait datée de la veille.
 */
export function dateDuJour(reference: Date = new Date()): string {
  return `${reference.getFullYear()}-${deuxChiffres(reference.getMonth() + 1)}-${deuxChiffres(reference.getDate())}`;
}

/* ── Identité ───────────────────────────────────────────────────────── */

/** `ALAMI Karim` — nom en capitales, prénom en casse normale. */
export function formaterIdentite(nom: string, prenom: string): string {
  return `${nom.toLocaleUpperCase('fr-FR')} ${prenom}`;
}
